import { HttpQuery } from "@/interfaces/httpQuery";
import { AxiosResponse } from "axios";
import { TableHeader, TablePagination } from "helix-vue-components";
import Vue from "vue";
import { HttpData, HttpPageableResponse } from "../model/http.model";

/**
 * abstract service to manage http request and general services configurations
 */
export default abstract class Http2Service extends Vue {
  /**
   * dispatcher to pagination actions
   * @var string loadDispatcher
   */
  protected loadDispatcher!: string;

  /**
   * default pagination definition
   * @var TablePagination: pagination
   */
  protected pagination: TablePagination = {
    totalItems: 0,
    itemsPerPage: 10,
    itemsPerPageOptions: [5, 10, 20],
    currentPage: 1,
    from: 1,
    to: 10
  };

  /**
   * @return Promise<TablePagination>
   */
  public async getPagination(): Promise<TablePagination> {
    return this.pagination;
  }

  /**
   * @param response
   */
  public setPagination(response: HttpPageableResponse<any>) {
    this.pagination.totalItems = response.total;
    this.pagination.itemsPerPage = +response.per_page;
    this.pagination.currentPage = response.current_page;
    this.pagination.from = response.from;
    this.pagination.to = response.to;
  }

  /**
   * sort action to make a request
   * @param params
   */
  public sortQuery(params: TableHeader) {
    return `${!params.descending ? "-" : ""}${params.value || ""}`;
  }

  /**
   * Exposed GET method for making requests to the API.
   * @return Promise<HttpData<HttpPageableResponse<any>>>
   * @param uri: string
   * @param query?: HttpQuery
   */
  protected async get(
    uri: string,
    query?: HttpQuery
  ): Promise<HttpData<HttpPageableResponse<any>>> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: uri,
      params: query
    });
    this.setPagination(response.data.data);
    return response.data;
  }

  /**
   * Exposed GET BY ID method for making requests to the API.
   * @return Promise<HttpData<HttpPageableResponse<any>>>
   * @param uri: string
   * @param id: number
   * @param query?: HttpQuery
   */
  protected async getByID(
    uri: string,
    id: number,
    query?: HttpQuery
  ): Promise<HttpData<any>> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: `${uri}/${id}`,
      params: query
    });
    return response.data.data;
  }

  /**
   * Exposed POST method for making requests to the API.
   * @return Promise
   * @param uri: string
   * @param model: any
   */
  protected async post(uri: string, model: any): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "POST",
      url: uri,
      data: model
    });
    return response.data;
  }

  /**
   * Exposed PUT method for making requests to the API.
   * @return Promise
   * @param uri: string
   * @param model: { [key: string]: string }
   * @param data: any
   */
  protected async put(
    uri: string,
    model: { [key: string]: string },
    data: any
  ): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "PUT",
      url: `${uri}/${model.id}`,
      data
    });

    return response.data;
  }

  /**
   * Exposed DELETE method for making requests to the API.
   * @return Promise
   * @param uri: string
   * @param model: { [key: string]: string }
   * @param force?: boolean
   */
  protected async delete(
    uri: string,
    model: { [key: string]: string },
    force?: boolean
  ): Promise<any> {
    const response: AxiosResponse = await Vue.axios({
      method: "DELETE",
      url: `${uri}/${model.id}`,
      data: force ? { force_delete: true } : undefined
    });
    return response.data;
  }
}
